import { ApiError } from 'schemas/ApiError.schema';

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export type ApiFetchOptionsParams = {
  method: HttpMethod;
  accessToken: string;
  body?: unknown;
};

export type ApiFetchParams = {
  path: string;
  method: HttpMethod;
  accessToken: string;
  body?: unknown;
};

async function getApiError(response: Response): Promise<ApiError> {
  try {
    const errorData = await response.json();
    return {
      status: response.status,
      message: errorData.message || errorData.error || `Request failed with status ${response.status}`,
    };
  } catch {
    return {
      status: response.status,
      message: `Request failed with status ${response.status}: ${response.statusText}`,
    };
  }
}

export const createAuthHeaders = (accessToken: string) => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${accessToken}`,
});

export const createFetchOptions = ({
  method,
  accessToken,
  body,
}: ApiFetchOptionsParams): Record<string, unknown> => {
  const options = {
    method,
    headers: createAuthHeaders(accessToken),
  } as Record<string, unknown>;

  if (body !== undefined) {
    (options as Record<string, unknown> & { body: string }).body = JSON.stringify(body);
  }

  return options;
};

export const buildApiUrl = (path: string): string => {
  const baseUrl = import.meta.env.VITE_BASE_API_URL || '';
  const normalizedPath = path.startsWith('/') ? path.slice(1) : path;
  return `${baseUrl}${normalizedPath}`;
};

export async function handleApiResponse<T>(response: Response): Promise<T> {
  if (response.ok) {
    return await response.json() as T;
  }

  const apiError = await getApiError(response);
  throw new Error(apiError.message);
}

export const apiFetch = async <T>({
  path,
  method,
  accessToken,
  body,
}: ApiFetchParams): Promise<T> => {
  const options = createFetchOptions({
    method,
    accessToken,
    body,
  });

  const response = await fetch(buildApiUrl(path), options);
  return handleApiResponse<T>(response);
};
